import { VisitorService } from './../../../shared/services/visitor.service';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { switchMap, takeUntil, tap, timer } from 'rxjs';
import { BaseComponent } from 'src/app/common/components/base.component';
import { ConfigService } from 'src/app/common/services/config/config.service';
import { SeoService } from 'src/app/common/services/seo.service/seo.service';
import { isEmptyArray, isNullOrUndefined } from 'src/app/common/utils/object.extensions';
import { similarity } from 'src/app/common/utils/string.extensions';
import { EventTimeService } from 'src/app/modules/event-calendar/services/event.time.service';
import { VisitorEventService } from 'src/app/modules/event-calendar/services/visitor.event.service';
import { VisitorPlaceService } from 'src/app/modules/event-calendar/services/visitor.place.service';
import { GoogleMapComponent } from 'src/app/modules/shared/components/google.map/google.map.component';
import { ImMessageBoxComponent } from 'src/app/modules/shared/components/im.message.box/im.message.box.component';
import { IMapMarkerWithCustomData } from 'src/app/modules/shared/models/place.marker';
import {
  AIModel,
  IAIResponse,
  ITravelAssistantMessage,
} from 'src/app/modules/shared/models/travel.assistant';
import { TravelBuddySettings } from 'src/app/modules/shared/models/travel.buddy.settings';
import { VisitorEvent } from 'src/app/modules/shared/models/visitor.event';
import { VisitorPlace } from 'src/app/modules/shared/models/visitor.place';
import { GoogleMapService } from 'src/app/modules/shared/services/google.map.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { SharedModule } from 'src/app/modules/shared/shared.module';
import { ShadowStyleLoaderService } from 'src/app/common/services/shadow.style.loader/shadow.style.loader.service';

@Component({
  selector: 'ig-ai-buddy-place-listing',
  standalone: true,
  templateUrl: './ai.buddy.place.listing.component.html',
  styleUrls: ['./ai.buddy.place.listing.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  host: {
    'id': 'ig-ai-buddy',
    style: 'font-family: Roboto, sans-serif',
  },
  imports: [
    CommonModule,
    FormsModule,
    ImMessageBoxComponent,
    SharedModule, // Contains GoogleMapComponent and other shared dependencies
    MatIconModule,
    MatSlideToggleModule,
  ],
})
export class AiBuddyPlaceListingComponent extends BaseComponent implements OnInit, AfterViewInit {
  @HostBinding('class') hostClass = 'ig-ai-buddy-place-listing';

  left: string;
  right: string;
  top: string;
  bottom: string;
  isShowCloseBtns = true;
  containerMode: string;

  aiModel: AIModel = 'chatgpt-assistant';
  /* the content in the landing page is showing up one by one */
  showLandscreenContent: boolean[] = [];
  places: (VisitorPlace & { ImGoingLink: string })[] = [];
  events: (VisitorEvent & { ImGoingLink: string })[] = [];
  msg: string = '';
  msgHistory: ITravelAssistantMessage[] = [];
  isScreenExpanded = false;

  isShowMap = false;
  mapMarkers: IMapMarkerWithCustomData<VisitorPlace | VisitorEvent>[] = [];
  center: google.maps.LatLngLiteral = null;
  directionLocations: { id; name; address; lat; lng; stopover }[] = [];
  directionType: 'normal' | 'optimized' | undefined = undefined;

  @ViewChild('map', { static: false }) googleMap: GoogleMapComponent;

  /*
    for AI answer, will match the suggested places with ImGoing places
    AI suggested place names are in bold
    the following words do not need to match */
  noMatchForWords = [
    'Mid-morning',
    'Morning',
    'Midday',
    'Lunch',
    'Late Afternoon',
    'Afternoon',
    'Early Evening',
    'Evening',
    'Dining',
  ];

  /* Sometimes OpenAI would give us some places that are not in ImGoing, then we search them in Google (at server side) and add to this list, to use them on the map */
  searchedPlaces = [];

  public target = '';
  public intro = '';
  public question1 = `What are some of the must-see attractions in {target}?`;
  public question2 = `Please suggest a 2-day itinerary in {target}.`;
  public travelBuddySettings: TravelBuddySettings;

  @Input() paramFromParent: string;

  @Input() template: TemplateRef<any>;
  @Input() context: any;

  @ViewChild('immessager', { static: false }) immessager: ImMessageBoxComponent;

  retryWhenAnswerContains = ['unable to access'];
  answerProcessor: (data: IAIResponse) => IAIResponse;

  constructor(
    public activatedRoute: ActivatedRoute,
    private _visitorService: VisitorService,
    private _googleMapService: GoogleMapService,
    private _placeService: VisitorPlaceService,
    private _eventService: VisitorEventService,
    private _eventTimeService: EventTimeService,
    private _seoService: SeoService,
    private _renderer: Renderer2,
    public cdRef: ChangeDetectorRef,
    private shadowStyleLoader: ShadowStyleLoaderService,
  ) {
    super({ activatedRoute });

    if (this.hubName) {
      _placeService.getDestinationPlaceFeeds(this.hubName).subscribe((places) => {
        this.places = places;
        console.log('places', places.length);
      });

      _eventService.getDestinationEventFeeds(this.hubName).subscribe((events) => {
        this.events = events;
        console.log('events', events.length);
      });

      for (let i = 1; i <= 20; i++) {
        this.noMatchForWords.push(`Day ${i}`);
      }

      if (this.frontendSettings?.ai?.travelBuddy?.cityName) {
        this.noMatchForWords.push(this.frontendSettings.ai.travelBuddy.cityName);
      }
    }
    // set position of the travel buddy toggle button, which can be set from the embedding script
    this.left = this.getScriptValue('travel-buddy-left') || 'auto';
    this.right = this.getScriptValue('travel-buddy-right') || '48px';
    this.top = this.getScriptValue('travel-buddy-top') || 'auto';
    this.bottom = this.getScriptValue('travel-buddy-bottom') || '32px';
    const rawShowCloseBtns = this.getScriptValue('travel-buddy-show-close-btns');
    this.isShowCloseBtns = isNullOrUndefined(rawShowCloseBtns) ? true : `${rawShowCloseBtns}`.trim().toLowerCase() === 'true';
    this.containerMode = this.getScriptValue('travel-buddy-container-mode') || 'web';

    this.answerProcessor = this.processAnswer.bind(this);
  }

  ngAfterViewInit(): void {
    this.injectStyle();
  }

  ngOnInit(): void {
    this.loadCustomSettings();
  }

  loadCustomSettings() {
    if (this.frontendSettings && !this.frontendSettings.ai?.travelBuddy?.cityName) {
      console.warn('Please set the city name');
    } else {
      this.target = this.frontendSettings?.ai?.travelBuddy?.cityName;
      const questions = this.frontendSettings?.ai?.travelBuddy?.ui?.questions;
      if (!isEmptyArray(questions)) {
        this.question1 = questions[0];
        if (questions.length > 1) {
          this.question2 = questions[1];
        } else {
          this.question2 = this.question2.replace('{target}', this.target);
        }
      } else {
        this.question1 = this.question1.replace('{target}', this.target);
        this.question2 = this.question2.replace('{target}', this.target);
      }

      this.travelBuddySettings = this.frontendSettings?.ai?.travelBuddy;
      this.intro =
        this.frontendSettings?.ai?.travelBuddy?.ui?.avatar?.intro ||
        `I'm your personal AI-powered travel assistant. Simply ask me a question, and I will provide you with relevant travel information and suggestions for your next trip to ${this.target}!`;
    }
  }

  get hasIntroduction() {
    if (!this.travelBuddySettings || !this.travelBuddySettings.ui.introduction) {
      return false;
    }

    // check if any of the properties of tis.introduction are not null or empty
    for (const key in this.travelBuddySettings.ui.introduction) {
      if (this.travelBuddySettings.ui.introduction[key]) {
        return true;
      }
    }
    return false;
  }

  tryAgain() {
    this.gotoChat(this.msg, true);
  }

  async gotoChat(msg: string, isTryAgain = false) {
    if (!msg && !isTryAgain) {
      return;
    }
    this.immessager.gotoChat(
      msg?.replace('{{target}}', this.target),
      isTryAgain,
      this.processAnswer.bind(this),
    );
  }

  loginStatusChanged(status: boolean) {
    console.log('login status in ai.buddy.place.list', status);
  }

  keypress($event: KeyboardEvent) {
    if ($event.key === 'Enter') {
      this.gotoChat(this.msg);
    }
  }

  processAnswer(data: IAIResponse): IAIResponse {
    // console.log('---------------------processAnswer called with-------------------', data);
    // console.log('---------------------Input placeSearch-------------------', data.placeSearch);
    // console.log(
    //   '---------------------Input placesMentioned-------------------',
    //   data.placesMentioned
    // );
    // console.log(
    //   '---------------------Input eventsMentioned-------------------',
    //   data.eventsMentioned
    // );

    // // data.answer = data.answer.replace(/\s+/g, ' ');  // only keep single blank
    // // data.answer = data.answer.replace(/<br>\s+<br>/g, '<br><br>');
    // // data.answer = data.answer.replaceAll('<br>-', '<br><br>-');  // add a line break before each list item
    // // data.answer = data.answer.replace(/<br>\d+\./g, '<br><br>$&');  // add a line break before each list item
    // // data.answer = data.answer.replace(/(<br>)+/g, '<br><br>'); // replace multiple line breaks with a single one
    // // data.answer = data.answer.replace(/<\/h3><br><br>/g, '</h3><br>'); // replace multiple line breaks with a single one after h3 title (which is usually "DAY 1" if you ask for an itinerary)
    // // data.answer = data.answer.replace(/<br><br><\/h3>/g, '<br></h3>'); // replace multiple line breaks with a single one after h3 title (which is usually "DAY 1" if you ask for an itinerary)
    // // data.answer = data.answer.replace(/\s<br><br>\s/g, '<br><br>');
    // // data.answer = data.answer.replace(/<br><br> - /g, ' - ');
    // // data.answer = data.answer.replace(/: -/g, ' -');
    // // data.answer = data.answer.replace(/<br><br> <\/p>/g, '<br></p>');

    // //data.answer = data.answer.replace(/&#39;/g, "'"); // to escape the symbols so that can match the event/place names
    // //data.answer = data.answer.replace(/&amp;/g, '&');
    // //data.answer = data.answer.replace(/&quot;/g, '"');

    // let placeNames = data.placesMentioned; //data.answer.match(/<em>(.*?)<\/em>/g);  // match event/place name, which are in italic
    // // let placeNamesUsingStrong = data.answer.match(/<strong>(.*?)<\/strong>/g);  // sometimes AI won't set the names in italic but in bold font
    // console.log('---------------------Extracted placeNames-------------------', placeNames);
    // console.log(
    //   '---------------------Available places count-------------------',
    //   this.places.length
    // );
    // console.log(
    //   '---------------------Available events count-------------------',
    //   this.events.length
    // );

    // // placeNames = (placeNames || []).concat(placeNamesUsingStrong);   // match both names in italic and bold
    // this.searchedPlaces = data.placeSearch;

    // // this.noMatchForWords.forEach((word) => {
    // //   data.answer = data.answer.replaceAll(`${word}:`, `<b>${word}:</b>`);
    // // });

    // this.isShowMap = false;
    // this.mapMarkers = [];
    // this.directionLocations = [];
    // let placeWithExternalLinks = [];

    // console.log('---------------------Starting place matching process-------------------');
    // console.log('---------------------placeNames exists-------------------', !!placeNames);
    // console.log(
    //   '---------------------placeNames length-------------------',
    //   placeNames?.length || 0
    // );

    // if (placeNames) {
    //   const contents = placeNames
    //     .filter((x) => x)
    //     .map((match) => match.replace(/<\/?em>/g, '').replace(/<\/?strong>/g, ''));
    //   const nameForMatching = contents.map((x) => x.replaceAll('&#39;', "'"));
    //   console.log('nameForMatching', nameForMatching);

    //   for (let i = 0; i < nameForMatching.length; i++) {
    //     console.log(
    //       `---------------------Processing place ${i + 1}/${nameForMatching.length}: ${
    //         nameForMatching[i]
    //       }-------------------`
    //     );

    //     if (
    //       this.noMatchForWords.any(
    //         (x) =>
    //           nameForMatching[i].toLowerCase().replace(':', '') === x.toLowerCase().replace(':', '')
    //       )
    //     ) {
    //       console.log(
    //         `---------------------Ignoring ${nameForMatching[i]} (in noMatchForWords)-------------------`
    //       );
    //       continue;
    //     }

    //     // Try exact match first using find
    //     // let place = this.places.find((x) => x.name === nameForMatching[i]);
    //     // console.log(`---------------------Direct name match found-------------------`, !!place);
    //     // console.log(`place find for ${nameForMatching[i]}`, place);

    //     // if (!place) {
    //     //   // Try placeSearch from AI (pre-matched by AI)
    //     //   const normalizedName = nameForMatching[i].toLowerCase().trim();
    //     //   const searchedPlace = data.placeSearch?.find((x) => x.name?.toLowerCase().trim() === normalizedName);
    //     //   if (searchedPlace && searchedPlace['searchedPlace']) {
    //     //     place = searchedPlace['searchedPlace'];
    //     //     console.log(`--------- place found in placeSearch ${nameForMatching[i]}`, place);
    //     //   } else {
    //     //     // SKIP expensive similarity matching - prevents 10-20 second UI blocking
    //     //     console.warn(`No exact match found for "${nameForMatching[i]}" - skipping fuzzy match for performance`);
    //     //   }
    //     // }

    //     let place = data.placeSearch?.find(
    //       (x) => x.name === nameForMatching[i] || similarity(x.name, nameForMatching[i]) > 0.8
    //     );
    //     if (place) {
    //       console.log(
    //         `************************* place found in placeSearch ${nameForMatching[i]}`,
    //         place
    //       );
    //       place = place.searchedPlace;
    //     } else {
    //       place = this.places.find((x) => x.name === nameForMatching[i]);
    //       console.log(`place find for ${nameForMatching[i]}`, place);
    //       if (!place) {
    //         let maxSimilarity = 0;
    //         let similarPlace = null;
    //         for (const p of this.places) {
    //           const nameSimilarity = similarity(p.name, nameForMatching[i]);
    //           if (nameSimilarity > maxSimilarity) {
    //             similarPlace = p;
    //             maxSimilarity = nameSimilarity;
    //           }
    //         }
    //         console.log('maxSimilarity', maxSimilarity);
    //         if (maxSimilarity >= 0.61) {
    //           place = similarPlace;
    //           console.log('similarPlace', place);
    //         } else {
    //           console.log('similarPlace -- ', similarPlace?.name);
    //         }
    //         console.log('\n');
    //       }
    //     }

    //     if (place && !this.directionLocations.any((x) => x.id === place._id)) {
    //       console.log('-- placeNames[i]: ' + placeNames[i], `will be replaced by ${place.name}`);
    //       //data.answer = data.answer.replace(placeNames[i] + ':', placeNames[i]);
    //       //data.answer = data.answer.replace(`<strong>${placeNames[i]}</strong>:`, placeNames[i]);
    //       // replace all placeNames[i] with place.name
    //       //data.answer = data.answer.replace(new RegExp(placeNames[i], 'g'), place.name);

    //       const placeLink = this.generatePlaceLink(place);
    //       //data.answer = data.answer
    //       // .replace(
    //       //   place.name,
    //       //   `<a class="ig-ai-link ${place._id}" target="_blank" href="${placeLink}">${
    //       //     place.name
    //       //   }</a>
    //       //   <div class="ig-ai-place ${place._id}">
    //       //     <img class="ig-ai-img" src="${
    //       //       place.cover.source || this.frontendSettings.customSettings.logo.url
    //       //     }" alt="${place.name}">
    //       //     <div class="ig-ai-info">
    //       //       <div class="flex items-center gap-1 ${!place.rating ? 'hidden' : ''}">
    //       //         <span class="text-xs">${place.rating}</span>
    //       //         <img src="${this._placeService.getRatingImg(
    //       //           place.rating
    //       //         )}" class="h-[14px]"></img>
    //       //       </div>
    //       //       <div class="text-xs">
    //       //         ${this._placeService.getDisplayCategory(place)}
    //       //       </div>
    //       //     </div>
    //       //     <div class="text-xs">
    //       //       <div class="text-sm font-semibold mb-0.5">
    //       //         ${place.name}
    //       //       </div>
    //       //       <div class="font-normal">${place.phone || ''}</div>
    //       //       <div class="font-normal">${place.website || ''}</div>
    //       //       <div class="font-normal">${place.address || ''}</div>
    //       //     </div>
    //       //   </div>`
    //       // );

    //       //console.log('============================== placeLink', placeLink);
    //       // if (!placeLink.includes('imgoing-place') && !placeLink.includes('places/details/')) {
    //       //   placeWithExternalLinks.push({
    //       //     googleId: place._id.toString(),
    //       //     externalLink: placeLink,
    //       //   });
    //       // }

    //       // <img src="https://iti-images.s3.amazonaws.com/imgs/clock.png" class="w-3 h-3 mt-[1px]"><span class="underline">See Hours</span>

    //       //console.log(`---------------------Creating map marker for place: ${place.name}-------------------`);
    //       if (place) {
    //         this.mapMarkers.push(this.createMapMarkersForPlace(place));

    //         this.directionLocations.push({
    //           id: place._id,
    //           name: place.name,
    //           address: place.address,
    //           lat: place.lat,
    //           lng: place.lng,
    //           stopover: true,
    //         });
    //         console.log(
    //           `---------------------Added place to directionLocations: ${place.name}-------------------`
    //         );
    //       }
    //     }
    //   }
    // }

    // let hasEventsInResponse = false;
    // let eventNames = data.eventsMentioned;

    // console.log('---------------------Events matching section-------------------');
    // console.log('---------------------eventNames exists-------------------', !!eventNames);
    // console.log(
    //   '---------------------eventNames length-------------------',
    //   eventNames?.length || 0
    // );

    // if (eventNames && eventNames.length > 0) {
    //   console.log('---------------------Starting events matching process-------------------');
    //   const contents = eventNames
    //     .filter((x) => x)
    //     .map((match) => match.replace(/<\/?em>/g, '').replace(/<\/?strong>/g, ''));
    //   const eventNameForMatching = contents.map((x) => x.replaceAll('&#39;', "'"));
    //   console.log('eventNameForMatching', eventNameForMatching);

    //   for (let i = 0; i < eventNameForMatching.length; i++) {
    //     if (
    //       this.noMatchForWords.any(
    //         (x) =>
    //           eventNameForMatching[i].toLowerCase().replace(':', '') ===
    //           x.toLowerCase().replace(':', '')
    //       )
    //     ) {
    //       console.log(`ignored ${eventNameForMatching[i]}\n`);
    //       continue;
    //     }

    //     let event = this.events.find((x) => x.name === eventNameForMatching[i]);
    //     console.log(`eventNames[i]: `, eventNames[i]);
    //     console.log(`event find for ${eventNameForMatching[i]}`, event);
    //     if (!event) {
    //       event = this.getSimilarEvent(eventNameForMatching[i]);

    //       // sometimes OpenAI may give us event name + at location (e.g., the original event name is "The Marshall Tucker Band" but OpenAI give us "The Marshall Tucker Band at the Great Ogeechee Music + Seafood Festival")
    //       // here is to remove the text after at and check again
    //       if (!event && eventNameForMatching[i].includes(' at ')) {
    //         const halfName = eventNameForMatching[i].split(' at ')[0];
    //         console.log(`eventNameForMatching[i] has at, half name ======= `, halfName);
    //         event = this.getSimilarEvent(halfName);
    //       }
    //       console.log('\n');
    //       if (!event) {
    //         let maxSimilarity = 0;
    //         let similarEvent = null;
    //         let startsWith = null;
    //         for (const e of this.events) {
    //           const nameSimilarity = similarity(e.name, eventNameForMatching[i]);
    //           if (nameSimilarity > maxSimilarity) {
    //             similarEvent = e;
    //             maxSimilarity = nameSimilarity;
    //           }
    //           if (e.name.toLowerCase().startsWith(eventNameForMatching[i].toLowerCase())) {
    //             startsWith = e;
    //           }
    //         }
    //         console.log('maxSimilarity', maxSimilarity);
    //         if (maxSimilarity >= 0.61) {
    //           event = similarEvent;
    //           console.log('similarEvent', event);
    //         } else if (startsWith) {
    //           event = startsWith;
    //           console.log('startsWith', event);
    //         } else {
    //           console.log('similarEvent -- ', similarEvent?.name);
    //         }
    //       }
    //     }

    //     if (event && !this.directionLocations.any((x) => x.id === event._id)) {
    //       this._eventTimeService.processEventTimes(event);
    //       //data.answer = data.answer.replace(eventNames[i] + ':', eventNames[i]);
    //       data.answer = data.answer;
    //       // .replace(
    //       //   eventNames[i],
    //       //   `<a class="ig-ai-link ${event._id}" target="_blank" href="${this.generateEventLink(
    //       //     event
    //       //   )}">${event.name}</a>
    //       //   <div class="ig-ai-place ${event._id}">
    //       //   <img class="ig-ai-img" src="${event.cover.source}" alt="${event.name}">
    //       //     <div class="text-xs">
    //       //       <div class="text-sm font-semibold mb-0.5">
    //       //         ${event.name}
    //       //       </div>
    //       //       <div class="font-normal">${event.timeOneLine || ''}</div>
    //       //       <div class="font-normal">${event.address.address || ''}</div>
    //       //     </div>
    //       //   </div>
    //       //   `
    //       // );
    //       //data.answer = data.answer.replace(/(<br>)+/g, '<br><br>');
    //       //data.answer = data.answer.replace(/<\/h3><br><br>/g, '</h3><br>'); // replace multiple line breaks with a single one after h3 title (which is usually "DAY 1" if you ask for an itinerary)
    //       //data.answer = data.answer.replace(/<br><br><\/h3>/g, '<br></h3>'); // replace multiple line breaks with a single one after h3 title (which is usually "DAY 1" if you ask for an itinerary)
    //       hasEventsInResponse = true;

    //       if (!event.isOnlineEvent) {
    //         this.mapMarkers.push(this.createMapMarkersForEvent(event));
    //         this.directionLocations.push({
    //           id: event._id,
    //           name: event.name,
    //           address: event.address,
    //           lat: event.address.lat,
    //           lng: event.address.lng,
    //           stopover: true,
    //         });
    //       }
    //     }
    //   }
    // }

    //data.answer = data.answer.replace(/<p><\/p>/g, '');
    //data.answer = data.answer.replace(/(<br>)+/g, '<br><br>');
    //data.answer = data.answer.replace(/<\/h3><br><br>/g, '</h3><br>'); // replace multiple line breaks with a single one after h3 title (which is usually "DAY 1" if you ask for an itinerary)
    //data.answer = data.answer.replace(/<br><br><\/h3>/g, '<br></h3>'); // replace multiple line breaks with a single one after h3 title (which is usually "DAY 1" if you ask for an itinerary)
    // const h3Match = data.answer.match(/<h3>(.*?)<br>/g);
    // if (h3Match) {
    //   for (const match of h3Match) {
    //     const index = data.answer.indexOf(match);
    //     if (!data.answer.substring(index, match.length + index + 6).includes('</h3>')) {
    //       data.answer = data.answer.replace(match, match + '</h3>');
    //     }
    //   }
    // }

    // add a client website event widget link
    if (
      // (hasEventsInResponse || data.askForEvents) &&
      data.askForEvents &&
      this.frontendSettings.contacts.eventCalendarURL
    ) {
      const eventWidgetURL = this.frontendSettings.contacts.eventCalendarURL;
      const btn = `<a class="w-full h-8 rounded !text-white !no-underline flex justify-center items-center bg-blue-400 cursor-pointer mt-1" target="_blank" href="${eventWidgetURL}">View All Events</a>`;
      const moreEventsTips = [
        `Discover even more exciting events here:`,
        `Want to see what else is happening? Check out our event page:`,
        `Looking for more events? Click here:`,
        `Explore our full calendar of events for all the latest happenings in ${this.target}, click here:`,
        `For a complete list of events, please visit our event page:`,
      ];
      data.answer +=
        data.answer.trim().endsWith('<br><br></p>') || data.answer.trim().endsWith('<br><br>')
          ? ''
          : '<br>';
      data.answer += `${moreEventsTips.getRandom()}<br>${btn}`;
    }

    if (data.askForItinerary && this.frontendSettings.contacts.moreToursURL) {
      const itineraryWidgetURL = this.frontendSettings.contacts.moreToursURL;
      const btn = `<a class="w-full h-8 rounded !text-white !no-underline flex justify-center items-center bg-orange-400 cursor-pointer mt-1" target="_blank" href="${itineraryWidgetURL}">View More Itineraries</a>`;
      const itineraryTips = [
        `Explore Our Itineraries! 🌟 Check out our Itinerary page for travel ideas!`,
        `Plan Your Perfect Trip ✈️ Find custom itineraries on our Itinerary page!`,
        `Need Travel Ideas? 🌍 Visit our Itinerary page for travel suggestions!`,
        `Your Next Adventure Awaits! See our Itinerary page for curated travel plans!`,
        `Dreaming of Your Next Trip? Discover itineraries on our Itinerary page!`,
      ];
      data.answer +=
        data.answer.trim().endsWith('<br><br></p>') || data.answer.trim().endsWith('<br><br>')
          ? ''
          : '<br>';
      data.answer += `${itineraryTips.getRandom()}<br>${btn}`;
    }

    // console.log(`====================================================================`);
    // console.log('placeWithExternalLinks', placeWithExternalLinks);
    // // for places with external links, these places might just be added to db
    // // so request again to get the place client links
    // console.log('%cplaceWithExternalLinks', 'color: lime', placeWithExternalLinks);
    // for places with external links, these places might just be added to db
    // so request again to get the place client links
    // if (placeWithExternalLinks.length) {
    //   if (this.frontendSettings.ai.travelBuddy.isStandalone) {
    //     timer(8000) // wait for place to be saved to db
    //       .pipe(
    //         switchMap(() =>
    //           this._placeService.getPlacesByGoogleIds(
    //             this.hubName,
    //             placeWithExternalLinks.map((x) => x.googleId)
    //           )
    //         )
    //       )
    //       .subscribe((places) => {
    //         for (const place of places) {
    //           let placeInCache = this.places.find((x) => x.id === place.id);
    //           console.log('%c===== placeInCache', 'color: lime', placeInCache);
    //           if (!placeInCache) {
    //             this.places.push(place);
    //             console.log('%c===== this.places updated', 'color: lime', this.places);
    //           } else {
    //             placeInCache.cover = place.cover;
    //             console.log('%c===== placeInCache updated', 'color: lime', placeInCache);
    //           }
    //         }
    //       });
    //   } else {
    //     timer(8000)
    //       .pipe(
    //         switchMap(() =>
    //           this._placeService.getPlacesByGoogleIds(
    //             // this._placeService.getDestinationPlaceFeeds(
    //             this.hubName,
    //             placeWithExternalLinks.map((x) => x.googleId)
    //           )
    //         )
    //       )
    //       .subscribe((places) => {
    //         console.log(
    //           '%c===== get places with external links from server',
    //           'color: lime',
    //           places
    //         );
    //         let aiShadowRoot = this.aiBuddyShadowRoot;
    //         if (!aiShadowRoot) {
    //           aiShadowRoot = this.shadowRoot;
    //         }

    //         for (const place of places) {
    //           const ori = placeWithExternalLinks.find((x) => x.googleId === place.id);
    //           const newLink = this.generatePlaceLink(place);
    //           console.log('===== ori', ori);
    //           console.log('===== newLink', newLink);
    //           if (ori) {
    //             const hrefs = Array.from(aiShadowRoot.querySelectorAll('.ig-ai-link.' + place.id));
    //             for (const href of hrefs) {
    //               href.setAttribute('href', newLink);
    //               console.log('================ href', href);
    //             }
    //           }
    //           let placeInCache = this.places.find((x) => x.id === place.id);
    //           console.log('%c===== placeInCache', 'color: lime', placeInCache);
    //           if (!placeInCache) {
    //             this.places.push(place);
    //             console.log('%c===== this.places updated', 'color: lime', this.places);
    //           }
    //         }
    //       });
    //   }
    // }

    // Process DOM updates immediately using requestAnimationFrame
    // requestAnimationFrame(() => {
    //   let aiShadowRoot = this.aiBuddyShadowRoot;
    //   if (!aiShadowRoot) {
    //     aiShadowRoot = this.shadowRoot;
    //   }
    //   const answer = Array.from(aiShadowRoot.querySelectorAll('.ig-im-answer')).last();

    //   const links = answer?.querySelectorAll('.ig-ai-link');
    //   console.log('links', links);

    //   // Show map if there are links OR if there are places mentioned (for streaming completion)
    //   console.log('---------------------Map visibility decision-------------------');
    //   console.log('---------------------links.length-------------------', links?.length || 0);
    //   console.log('---------------------placeNames exists-------------------', !!placeNames);
    //   console.log(
    //     '---------------------placeNames.length-------------------',
    //     placeNames?.length || 0
    //   );
    //   console.log(
    //     '---------------------mapMarkers.length-------------------',
    //     this.mapMarkers.length
    //   );

    //   if ((links && links.length > 0) || (placeNames && placeNames.length > 0)) {
    //     console.log('---------------------Setting isShowMap to true-------------------');
    //     this.isShowMap = true;

    //     requestAnimationFrame(() => {
    //       for (const link of Array.from(links || [])) {
    //         const itemId = Array.from(link.classList).last();
    //         let item =
    //           this.places.find((x) => x._id === itemId) ||
    //           this.events.find((x) => x._id === itemId);

    //         if (!item) {
    //           item = this.searchedPlaces.find((x) => x.searchedPlace._id === itemId)?.searchedPlace;
    //         }

    //         this._renderer.listen(link, 'mouseover', () => {
    //           const marker = this.googleMap.mapMarkerObjs.find((x) => x.getTitle() === itemId);

    //           if (this._visitorService.getItemType(item) === 'event') {
    //             this.googleMap.showEventInfoWindow(marker, {
    //               ...marker,
    //               data: item as VisitorEvent,
    //             } as IMapMarkerWithCustomData<VisitorEvent>);
    //           } else {
    //             this.googleMap.showPlaceInfoWindow(marker, {
    //               ...marker,
    //               data: item as VisitorPlace,
    //             } as IMapMarkerWithCustomData<VisitorPlace>);
    //           }
    //         });
    //       }

    //       this.adjustMapHeight();
    //     });
    //   }
    // });

    // console.log('---------------------processAnswer returning-------------------', {
    //   hasMapMarkers: this.mapMarkers.length > 0,
    //   mapMarkersCount: this.mapMarkers.length,
    //   isShowMap: this.isShowMap,
    //   directionLocationsCount: this.directionLocations.length,
    // });

    return data;
  }

  requestPlaces() {}

  getSimilarEvent(target: string) {
    // SKIP expensive similarity matching - return undefined immediately
    // This prevents UI blocking. AI should provide exact event names.
    console.warn(`No exact match for event "${target}" - skipping fuzzy match for performance`);
    return undefined;
  }

  adjustMapHeight() {
    // adjust map height to match the left answers panel's height
    let aiShadowRoot = this.aiBuddyShadowRoot;
    if (!aiShadowRoot) {
      aiShadowRoot = this.shadowRoot;
    }
    const answer = Array.from(aiShadowRoot.querySelectorAll('.ig-im-answer')).last();

    // Use requestAnimationFrame for proper DOM timing
    requestAnimationFrame(() => {
      const size = answer?.getBoundingClientRect();
      const mapArea = Array.from(this.shadowRoot.querySelectorAll('.ig-im-adding-expanded')).last();
      if (mapArea && size) {
        (mapArea as HTMLElement).style.height = `${size.height}px`;
        const map = mapArea.querySelector('ig-google-map');
        if (map) {
          (map as HTMLElement).style.height = `${size.height}px`;
        }
      }
    });
  }

  drawDirectionRoute() {
    this.mapMarkers = [];
    this.directionLocations.forEach((marker, index) => {
      // for the yellow order marker
      this.mapMarkers.push({
        position: {
          lat: marker.lat,
          lng: marker.lng,
        },
        options: {
          icon: {
            url: 'https://imgoingcalendar.com/assets/imgs/tp-marker-2.png',
            // anchor: new google.maps.Point(30, 50),
            scaledSize: new google.maps.Size(25, 25),
          } as google.maps.Icon,
          zIndex: 10,
          // optimized: true,
        },
        label: {
          text: (++index).toString(),
          fontWeight: 'bold',
        },
      } as IMapMarkerWithCustomData<VisitorPlace>);
    });

    this.googleMap.drawDirectionRoute(this.directionLocations);
    this.directionType = 'normal';
  }

  getDirections() {
    if (this.directionLocations.length === 0) return;

    const addresses = this.directionLocations.map((stop) =>
      String(stop.address || '')
        .replace(/,?\s*null\b/gi, '')
        .trim(),
    );

    const destination = addresses[addresses.length - 1];
    const intermediateWaypoints = addresses.slice(0, -1);

    let url = 'https://www.google.com/maps/dir/?api=1';
    url += `&destination=${encodeURIComponent(destination)}`;
    if (intermediateWaypoints.length > 0) {
      url += `&waypoints=${encodeURIComponent(intermediateWaypoints.join('|'))}`;
    }
    url += '&travelmode=driving';

    const ua = navigator.userAgent;

    const isIOSWebView =
      /iPad|iPhone|iPod/.test(ua) &&
      /AppleWebKit/.test(ua) &&
      !/Safari\//.test(ua);

    const isAndroidWebView = /Android/.test(ua) && /\bwv\b/.test(ua);

    if (isIOSWebView || isAndroidWebView) {
      window.location.href = url;
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }

  optimizeDirectionRouteByDistance() {
    const start = this.directionLocations[0];

    let waypoints = [start];
    while (waypoints.length < this.directionLocations.length) {
      const point = waypoints.last();
      const nearest = this.getNeareatPoint(
        point,
        this.directionLocations.filter((x) => x != point && !waypoints.includes(x)),
      );
      waypoints.push(nearest);
    }

    console.log('waypoints in by distance', waypoints);

    this.googleMap.drawDirectionRoute(waypoints);

    this.mapMarkers = [];
    // Use requestAnimationFrame for proper timing after route drawing
    requestAnimationFrame(() => {
      waypoints.forEach((x, index) => {
        this.addMapMarker(x.lat, x.lng, index + 1, x.id);
      });
    });

    this.directionType = 'optimized';
  }

  getNeareatPoint(start, targetPoints: any[]) {
    let minDistance = 99999,
      nearestPoint = null;

    for (const point of targetPoints) {
      const distance = this._googleMapService.getDistanceFromLatLng(
        start.lat,
        start.lng,
        point.lat,
        point.lng,
      );
      if (distance < minDistance && distance > 0) {
        minDistance = distance;
        nearestPoint = point;
      }
    }

    return nearestPoint;
  }

  optimizeDirectionRoute() {
    const start = this.directionLocations[0];
    let maxDistance = 0,
      farestPoint = null;
    for (const point of this.directionLocations) {
      const distance = this._googleMapService.getDistanceFromLatLng(
        start.lat,
        start.lng,
        point.lat,
        point.lng,
      );
      if (distance > maxDistance) {
        maxDistance = distance;
        farestPoint = point;
      }
    }

    // farestPoint = this.directionLocations.find(
    //   (x) => x.name === 'The Olde Pink House'
    // );
    console.log('maxDistance', maxDistance);
    console.log('farestPoint', farestPoint.name);

    // move farestPoint to the last one of this.directionLocations
    this.directionLocations.splice(
      this.directionLocations.length - 1,
      0,
      this.directionLocations.splice(this.directionLocations.indexOf(farestPoint), 1)[0],
    );

    this.googleMap.directionsResults$.pipe(takeUntil(this.isDestroyed)).subscribe(async (data) => {
      console.log('direction', data);

      this.mapMarkers = [];
      let waypoints = [
        {
          lat: data.request.origin['location'].lat(),
          lng: data.request.origin['location'].lng(),
          stopover: true,
        },
      ];

      data.request.waypoints.forEach((waypoint) => {
        waypoints.push({
          lat: waypoint.location['location'].lat(),
          lng: waypoint.location['location'].lng(),
          stopover: true,
        });
      });
      waypoints.push({
        lat: data.request.destination['location'].lat(),
        lng: data.request.destination['location'].lng(),
        stopover: true,
      });
      console.log('waypoints', waypoints);

      // Use requestAnimationFrame for proper timing after route optimization
      requestAnimationFrame(() => {
        waypoints.forEach((marker, index) => {
          // for the yellow order marker
          this.mapMarkers.push({
            position: {
              lat: marker.lat,
              lng: marker.lng,
            },
            options: {
              icon: {
                url: 'https://imgoingcalendar.com/assets/imgs/tp-marker-2.png',
                // anchor: new google.maps.Point(30, 50),
                scaledSize: new google.maps.Size(25, 25),
              } as google.maps.Icon,
              zIndex: 10,
              // optimized: true,
            },
            label: {
              text: (++index).toString(),
              fontWeight: 'bold',
            },
          } as IMapMarkerWithCustomData<VisitorPlace>);
        });
      });
    });

    this.googleMap.drawDirectionRoute(this.directionLocations, google.maps.TravelMode.DRIVING, {
      optimizeWaypoints: true,
    });
  }

  addMapMarker(lat, lng, label, id) {
    this.mapMarkers.push({
      position: {
        lat: lat,
        lng: lng,
      },
      options: {
        icon: {
          url: 'https://imgoingcalendar.com/assets/imgs/tp-marker-2.png',
          // anchor: new google.maps.Point(30, 50),
          scaledSize: new google.maps.Size(25, 25),
        } as google.maps.Icon,
        zIndex: 10,
      },
      label: {
        text: label.toString(),
        fontWeight: 'bold',
      },
      title: id,
    } as IMapMarkerWithCustomData<VisitorPlace>);
  }

  generatePlaceLink(place: VisitorPlace & { clientURL?: string; ImGoingLink: string }): string {
    if (this.isScriptSolution) {
      return place.clientURL;
    }

    return place.ImGoingLink;
  }

  generateEventLink(event: VisitorEvent & { ImGoingLink: string }): string {
    if (this.isScriptSolution) {
      if (this.frontendSettings?.contacts?.eventCalendarURL) {
        return `${this.frontendSettings.contacts.eventCalendarURL}?imgoing-event=${event.nameId}`;
      }
    }

    return event.ImGoingLink;
  }

  get hasImg() {
    let aiShadowRoot = this.aiBuddyShadowRoot;
    if (!aiShadowRoot) {
      aiShadowRoot = this.shadowRoot;
    }
    const answers = aiShadowRoot.querySelectorAll('.ig-im-answer');
    if (answers.length > 0) {
      const imgs = Array.from(answers).last().querySelectorAll('.ig-ai-img');
      return imgs.length > 0;
    }
    return false;
  }

  hideImg() {
    const placeImgs = this.shadowRoot.querySelectorAll('.ig-ai-place');
    const cards: HTMLElement[] = Array.from(placeImgs) as HTMLElement[];
    cards.forEach((ele) => {
      ele.classList.contains('hidden')
        ? ele.classList.remove('hidden')
        : ele.classList.add('hidden');
    });

    this.adjustMapHeight();
  }

  onMsgCreated(data: { lastMsg: ITravelAssistantMessage }) {
    console.log('--------------------onMsgCreated-------------------', data);

    this.msgHistory.push(data.lastMsg);

    if (data.lastMsg.type === 'AI') {
      console.log('---------------------Processing AI message in AI Buddy-------------------');
      // Convert ITravelAssistantMessage to IAIResponse for processAnswer
      const aiResponse: IAIResponse = {
        answer: data.lastMsg.msg,
        model: data.lastMsg.model,
        assistantId: undefined,
        threadId: undefined,
        timeConsumed: data.lastMsg.timeConsumed,
        questions: data.lastMsg.questions,
        isAskingAboutEvents: false,
        furtherChat: undefined,
        placeSearch: data.lastMsg.placeSearch,
        placesMentioned: data.lastMsg.placesMentioned,
        eventsMentioned: data.lastMsg.eventsMentioned,
        askForEvents: data.lastMsg.askForEvents,
        askForItinerary: data.lastMsg.askForItinerary,
        summary: data.lastMsg.summary,
        totalSystemPromptTime: data.lastMsg.totalSystemPromptTime,
        timeToFirstChunk: data.lastMsg.timeToFirstChunk,
        toolCalls: data.lastMsg.toolCalls,
      };
      this.processAnswer(aiResponse);
    } else {
      console.log(
        '---------------------Message type is not AI, skipping processing-------------------',
      );
    }
  }

  createMapMarkersForPlace(
    place: VisitorPlace | VisitorEvent,
  ): IMapMarkerWithCustomData<VisitorPlace> {
    return {
      position: {
        lat: place.lat,
        lng: place.lng,
      },
      // label: place.name,
      title: place._id,
      data: place,
    } as IMapMarkerWithCustomData<VisitorPlace>;
  }

  createMapMarkersForEvent(event: VisitorEvent): IMapMarkerWithCustomData<VisitorEvent> {
    return {
      position: {
        lat: event.address.lat,
        lng: event.address.lng,
      },
      // label: event.name,
      title: event._id,
      data: event,
    } as IMapMarkerWithCustomData<VisitorEvent>;
  }

  onMarkerClick(marker: IMapMarkerWithCustomData<VisitorPlace>) {
    console.log('onMarkerClick', marker);

    this.center = {
      lat: marker.position.lat,
      lng: marker.position.lng,
    } as google.maps.LatLngLiteral;

    const mapMarker = this.googleMap.mapMarkerObjs.find((x) => x.getTitle() === marker.title);
    const place = this.places.find((x) => x._id === marker.title);

    if (place) {
      this.googleMap.showPlaceInfoWindow(mapMarker, {
        ...mapMarker,
        data: place as VisitorPlace,
      } as IMapMarkerWithCustomData<VisitorPlace>);
      return;
    }

    const event = this.events.find((x) => x._id === marker.title);

    if (event) {
      this.googleMap.showEventInfoWindow(mapMarker, {
        ...mapMarker,
        data: event as VisitorEvent,
      } as IMapMarkerWithCustomData<VisitorEvent>);
    }
  }

  expandScreen($event: boolean) {
    this.isScreenExpanded = $event;
  }

  private injectStyle() {
    const aiBuddyShadowRoot = document.getElementById('ig-ai-buddy')?.shadowRoot;
    if (aiBuddyShadowRoot) {
      this.shadowStyleLoader.injectBaseStyles(aiBuddyShadowRoot);
    }

    timer(200)
      .pipe(
        tap(() => {
          this.cdRef.detectChanges();
        }),
      )
      .subscribe();
  }
}
